import { CustomerPricing, ICustomerPricingData } from '@/models';
import { Customer, ICustomerData } from '@/models/customer';
import {
  CustomerActivity,
  ICustomerActivityData
} from '@/models/customeractivity';
import { CustomerService } from '@/services/customer-service';
import {
  ICustomerFilterParams,
  ICustomerPaginatedListData
} from '@/services/customer-service.types';
import { ActionTree, GetterTree, MutationTree } from 'vuex';

export interface IFetchCustomerActionPayload {
  page?: number;
  filters?: ICustomerFilterParams;
}

const customerService = new CustomerService();

export const state = {
  customers: [] as ICustomerData[],
  activities: [] as ICustomerActivityData[],
  lastPromise: null as null | Promise<any>,
  count: 0
};

type CustomersState = typeof state;

export const mutations: MutationTree<CustomersState> = {
  SET_CUSTOMER_PRICING_ACTIVITIES(state, payload: ICustomerActivityData[]) {
    state.activities = payload;
  },

  SET_CUSTOMERS(state, payload: ICustomerData[]) {
    state.customers = payload;
  },

  ADD_CUSTOMER(state, payload: ICustomerData) {
    state.customers.push(payload);
  },

  UPDATE_CUSTOMER(state, payload: ICustomerData) {
    const index = state.customers.findIndex(
      (customer: ICustomerData) => customer.id === payload.id
    );
    state.customers.splice(index, 1, payload);
  },

  REMOVE_CUSTOMER(state, payload: string) {
    const index = state.customers.findIndex(
      (customer: ICustomerData) => customer.id === payload
    );
    state.customers.splice(index, 1);
  },

  UPDATE_CUSTOMER_PRICING(state, payload: ICustomerPricingData) {
    const index = state.customers.findIndex(
      (customer: ICustomerData) => customer.id === payload.customer
    );

    const customer = state.customers.find(
      (customer: ICustomerData) => customer.id === payload.customer
    );

    if (customer) {
      customer.contract = new CustomerPricing(payload);
      state.customers.splice(index, 1, customer);
    }
  },

  REMOVE_CUSTOMER_PRICING(state, payload: string) {
    const index = state.customers.findIndex(
      (customer: ICustomerData) => customer.id === payload
    );

    const customer = state.customers.find(
      (customer: ICustomerData) => customer.id === payload
    );

    if (customer) {
      customer.contract = null;
      state.customers.splice(index, 1, customer);
    }
  },

  SET_COUNT(state, payload: number): void {
    state.count = payload;
  },

  SET_LAST_PROMISE(state, payload: Promise<any>): void {
    state.lastPromise = payload;
  }
};

export const actions: ActionTree<CustomersState, CustomersState> = {
  createCustomer: async ({ commit }, payload: ICustomerData) =>
    customerService
      .createCustomer(payload)
      .then((customer: ICustomerData) => commit('ADD_CUSTOMER', customer)),

  fetchCustomerPricingHistory: async ({ commit }, params: string) =>
    customerService
      .fetchCustomerPricingHistory(params)
      .then((activities: ICustomerActivityData[]) => {
        commit('SET_CUSTOMER_PRICING_ACTIVITIES', activities);
      }),

  fetchCustomers: async (context, payload?: IFetchCustomerActionPayload) => {
    const promise = customerService.fetchCustomers(
      payload?.page,
      payload?.filters
    );

    context.commit('SET_LAST_PROMISE', promise);
    promise.then((customerData: ICustomerPaginatedListData) => {
      if (promise == context.state.lastPromise) {
        context.commit('SET_CUSTOMERS', customerData.results);
        context.commit('SET_COUNT', customerData.count);
        context.commit('SET_LAST_PROMISE', null);
      }
    });
  },

  fetchCustomer: async ({ commit }, payload: string) =>
    customerService.fetchCustomer(payload).then((customer: ICustomerData) => {
      const index = state.customers.findIndex(
        (v: ICustomerData) => v.id === customer.id
      );
      if (index === -1) {
        commit('ADD_CUSTOMER', customer);
      } else {
        commit('UPDATE_CUSTOMER', customer);
      }
    }),

  updateCustomer: async ({ state, commit }, payload: ICustomerData) =>
    customerService.updateCustomer(payload).then((customer: ICustomerData) => {
      const index = state.customers.findIndex(
        (v: ICustomerData) => v.id === customer.id
      );
      if (index === -1) {
        commit('ADD_CUSTOMER', customer);
      } else {
        commit('UPDATE_CUSTOMER', customer);
      }
    }),

  deleteCustomer: async ({ commit }, payload: string) =>
    customerService.deleteCustomer(payload).then(() => {
      commit('REMOVE_CUSTOMER', payload);
    }),

  addCustomerPricing: ({ commit }, payload: ICustomerPricingData) =>
    customerService
      .addCustomerPricing(payload)
      .then((contract: ICustomerPricingData) => {
        const index = state.customers.findIndex(
          (v: ICustomerData) => v.id === contract.customer
        );
        if (index !== -1) {
          commit('UPDATE_CUSTOMER_PRICING', contract);
        }
      }),

  updateCustomerPricingValidity: ({ commit }, payload: ICustomerPricingData) =>
    customerService
      .updateCustomerPricingValidity(payload)
      .then((contract: ICustomerPricingData) => {
        const index = state.customers.findIndex(
          (v: ICustomerData) => v.id === contract.customer
        );
        if (index !== -1) {
          commit('UPDATE_CUSTOMER_PRICING', contract);
        }
      }),

  deleteCustomerPricing: ({ commit }, payload: ICustomerPricingData) =>
    customerService.deleteCustomerPricing(payload).then(() => {
      const index = state.customers.findIndex(
        (v: ICustomerData) => v.id === payload.customer
      );
      if (index !== -1) {
        commit('REMOVE_CUSTOMER_PRICING', payload.customer);
      }
    }),

  uploadCustomer: async (_, payload: FormData) =>
    customerService.uploadCustomer(payload).then(res => res),

  uploadCustomerAddress: async (_, payload: FormData) =>
    customerService.uploadCustomerAddress(payload).then(res => res)
};

export const getters: GetterTree<CustomersState, CustomersState> = {
  getCustomerById: state => (id: string) => {
    const customer = state.customers.find(customer => customer.id === id);
    if (customer) {
      return new Customer(customer);
    }
    return undefined;
  },

  getCustomers: state =>
    state.customers.map(customer => new Customer(customer)),

  getCustomerCount(state): number {
    return state.count;
  },

  getCustomerPricingActivities: state =>
    state.activities.map(activity => new CustomerActivity(activity))
};
